import constant from "../../constant";
import router from "../../router";

const api = constant.api + "allowed_ips";

// initial state
const state = () => ({
  all: [],
  allowedIPs: [],
  allowedIP: {},
});

// getters
const getters = {
  getAll: (state) => {
    return state.all;
  },
  getAllowedIP: (state) => {
    return state.allowedIP;
  },
  getAllowedIPs: (state) => {
    return state.allowedIPs;
  },
};

// actions
const actions = {
  async getAll({ commit }) {
    try {
      // Get axios instance from Vue plugin
      const axiosInstance = this._vm.$api && this._vm.$api.instance 
        ? this._vm.$api.instance 
        : null;

      if (!axiosInstance) {
        console.warn("API instance not available for allowed IPs");
        return;
      }

      // Verify token is present
      const token = localStorage.getItem(constant.authToken);
      if (!token) {
        console.error("No auth token found in localStorage");
        console.error("Please login again. Token is missing.");
        throw new Error("Token d'authentification non trouvé. Veuillez vous reconnecter.");
      }

      // Log token info for debugging (only first few chars for security)
      if (process.env.NODE_ENV === "development") {
        const cleanToken = token.replace(/^"|"$/g, '');
        console.log("Token info:", {
          hasToken: !!token,
          tokenLength: cleanToken.length,
          tokenPreview: cleanToken.substring(0, 20) + "...",
          fullToken: cleanToken
        });
      }

      const response = await axiosInstance.get(api);
      let allowedIPs = response.data || [];
      commit("setAllowedIPs", allowedIPs);
      return allowedIPs;
    } catch (error) {
      console.error("Error fetching allowed IPs:", error);
      console.error("Error details:", {
        message: error.message,
        response: (error.response && error.response.data),
        status: (error.response && error.response.status),
        url: api
      });
      
      // If 401 Unauthorized, the token might be expired or invalid
      if (error.response && error.response.status === 401) {
        console.error("Authentication failed. Token might be expired or invalid.");
        console.error("Please check if you are logged in and the token is valid.");
      }
      
      // Don't throw error, just log it and set empty array
      // This allows the component to still work even if the API call fails
      commit("setAllowedIPs", []);
      return [];
    }
  },

  async show({ commit }, id) {
    try {
      // Get axios instance from Vue plugin
      const axiosInstance = this._vm.$api && this._vm.$api.instance 
        ? this._vm.$api.instance 
        : null;

      if (!axiosInstance) {
        throw new Error("API instance not available. Please refresh the page.");
      }

      const response = await axiosInstance.get(api + "/" + id);
      let allowedIP = response.data;
      commit("setAllowedIP", allowedIP);
    } catch (error) {
      console.error("Error fetching allowed IP:", error);
      throw error;
    }
  },

  async store({ commit, dispatch }, data) {
    try {
      // Get axios instance from Vue plugin
      const axiosInstance = this._vm.$api && this._vm.$api.instance 
        ? this._vm.$api.instance 
        : null;

      if (!axiosInstance) {
        throw new Error("API instance not available. Please refresh the page.");
      }

      const response = await axiosInstance.post(api, data);
      let message = response.data.message;
      let status = response.data.status;
      dispatch("notification/store", { status, message }, { root: true });
      if (status == "success") {
        // Optionally refresh the list
        await dispatch("getAll");
      }
      return response;
    } catch (error) {
      console.error("Error storing allowed IP:", error);
      const errorMessage = (error.response && error.response.data && error.response.data.message) || 
        "Erreur lors de l'ajout de l'IP autorisée.";
      dispatch("notification/store", { status: "error", message: errorMessage }, { root: true });
      throw error;
    }
  },

  async update({ commit, dispatch }, data) {
    try {
      // Get axios instance from Vue plugin
      const axiosInstance = this._vm.$api && this._vm.$api.instance 
        ? this._vm.$api.instance 
        : null;

      if (!axiosInstance) {
        throw new Error("API instance not available. Please refresh the page.");
      }

      const response = await axiosInstance.put(api + "/" + data.id, data);
      let message = response.data.message;
      let status = response.data.status;
      dispatch("notification/store", { status, message }, { root: true });
      if (status == "success") {
        await dispatch("getAll");
      }
      return response;
    } catch (error) {
      console.error("Error updating allowed IP:", error);
      const errorMessage = (error.response && error.response.data && error.response.data.message) || 
        "Erreur lors de la mise à jour de l'IP autorisée.";
      dispatch("notification/store", { status: "error", message: errorMessage }, { root: true });
      throw error;
    }
  },

  async findByIpAddress({ commit }, ipAddress) {
    try {
      // Get axios instance from Vue plugin
      const axiosInstance = this._vm.$api && this._vm.$api.instance 
        ? this._vm.$api.instance 
        : null;

      if (!axiosInstance) {
        throw new Error("API instance not available. Please refresh the page.");
      }

      // Get all allowed IPs and find by IP address
      const response = await axiosInstance.get(api);
      const allowedIPs = response.data || [];
      const foundIP = allowedIPs.find(ip => ip.ip_address === ipAddress);
      
      return foundIP || null;
    } catch (error) {
      console.error("Error finding allowed IP by address:", error);
      // If the API call fails, return null
      return null;
    }
  },

  async destroy({ commit, dispatch }, id) {
    try {
      console.log("destroy called with ID:", id);
      
      // Get axios instance from Vue plugin
      const axiosInstance = this._vm.$api && this._vm.$api.instance 
        ? this._vm.$api.instance 
        : null;

      if (!axiosInstance) {
        throw new Error("API instance not available. Please refresh the page.");
      }

      const response = await axiosInstance.delete(api + "/" + id);
      console.log("Delete response:", response);
      
      // Handle different response formats
      let message = (response.data && response.data.message) || "IP autorisée supprimée avec succès";
      let status = (response.data && response.data.status) || "success";
      
      // If response doesn't have status, check if it was successful (200-299)
      if (!(response.data && response.data.status) && response.status >= 200 && response.status < 300) {
        status = "success";
      }
      
      dispatch("notification/store", { status, message }, { root: true });
      
      if (status == "success") {
        // Refresh the list after deletion
        await dispatch("getAll");
      }
      
      return response;
    } catch (error) {
      console.error("Error destroying allowed IP:", error);
      console.error("Error details:", {
        message: error.message,
        response: (error.response && error.response.data),
        status: (error.response && error.response.status)
      });
      
      const errorMessage = (error.response && error.response.data && error.response.data.message) || 
        error.message ||
        "Erreur lors de la suppression de l'IP autorisée.";
      dispatch("notification/store", { status: "error", message: errorMessage }, { root: true });
      throw error;
    }
  },

  async destroyByIpAddress({ commit, dispatch }, ipAddress) {
    // Get $api from Vue plugin
    const $api = this._vm.$api;

    if (!$api || !$api.instance) {
      throw new Error("API plugin not available. Please refresh the page.");
    }

    // Verify token is present
    const token = localStorage.getItem(constant.authToken);
    if (!token) {
      throw new Error("Token d'authentification non trouvé. Veuillez vous reconnecter.");
    }

    console.log("Token found:", token ? "Yes" : "No");
    console.log("Making request to:", api);

    // Get all allowed IPs to find the one to delete
    // Use the instance directly to ensure interceptor is used
    const getAllResponse = await $api.instance.get(api);
    const allowedIPs = getAllResponse.data || [];
    
    const allowedIP = allowedIPs.find(ip => ip && ip.ip_address === ipAddress);
    
    if (!allowedIP || !allowedIP.id) {
      throw new Error(`IP autorisée non trouvée pour l'adresse: ${ipAddress}`);
    }

    // Use $api.delete which handles loading automatically
    // Pass confirm: false to skip the default confirmation dialog
    const deleteResponse = await $api.delete(api + "/" + allowedIP.id, { confirm: false });
    
    // Refresh the list after deletion
    if (deleteResponse && deleteResponse.status >= 200 && deleteResponse.status < 300) {
      await dispatch("getAll");
    }
    
    return deleteResponse;
  },
};

// mutations
const mutations = {
  setAllowedIPs(state, allowedIPs) {
    state.all = allowedIPs;
    state.allowedIPs = allowedIPs;
  },
  setAllowedIP(state, allowedIP) {
    state.allowedIP = allowedIP;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
